﻿import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { pluginBarraMinima, valoresExibiveis } from "../utils/chartMinBar";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const colors = {
  realized: "#003DA5",
  gap: "#0096FF",
  textPrimary: "#01054C",
  textSecondary: "#364153",
  gridLine: "#E5E7EB",
  borderLine: "#D1D5DB",
  white: "#fff",
};

const fmt2 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatM = (value) => {
  if (value == null || value === 0) return "";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${fmt2.format(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${fmt2.format(value / 1_000)}k`;
  return fmt2.format(value);
};


const buildOptions = (inicioMesIndex) => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 48 } },
  plugins: {
    legend: {
      position: "bottom",
      align: "start",
      labels: {
        font: { size: 11 },
        color: colors.textSecondary,
        padding: 16,
        usePointStyle: true,
        pointStyle: "rectRounded",
        pointStyleWidth: 14,
        boxHeight: 10,
      },
    },
    tooltip: {
      callbacks: {
        label: (ctx) => ` ${ctx.dataset.label}: ${formatM(ctx.raw)}`,
      },
    },
    datalabels: {
      anchor: "center",
      align: "center",
      color: colors.white,
      font: { size: 10, weight: "bold" },
      rotation: 0,
      formatter: (value) => formatM(value),
    },
    barAnnotations: { inicioMesIndex },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      border: { display: true, color: colors.borderLine },
      ticks: { font: { size: 13, weight: "bold" }, color: colors.textPrimary },
    },
    y: {
      stacked: true,
      display: false,
      beginAtZero: true,
    },
  },
});

const barAnnotationsPlugin = {
  id: "barAnnotations",
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;

    const yScale = scales.y;
    const { left, right, top, bottom } = chartArea;
    const tickCount = 5;
    const step = (yScale.max - yScale.min) / tickCount;

    ctx.save();
    ctx.lineWidth = 1;

    ctx.strokeStyle = colors.gridLine;
    for (let i = 0; i <= tickCount; i++) {
      const y = Math.round(yScale.getPixelForValue(yScale.min + step * i)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
      ctx.stroke();
    }

    ctx.strokeStyle = colors.borderLine;
    ctx.beginPath();
    ctx.moveTo(left + 0.5, top);
    ctx.lineTo(left + 0.5, bottom);
    ctx.stroke();

    ctx.restore();
  },
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart;
    const xScale = chart.scales.x;

    const getVisibleTotal = (colIndex) =>
      data.datasets.reduce((sum, ds, dsIndex) => {
        if (chart.getDatasetMeta(dsIndex).hidden) return sum;
        return sum + (ds.data[colIndex] || 0);
      }, 0);

    const getTopBarY = (colIndex) => {
      const gapMeta = chart.getDatasetMeta(1);
      const gapValue = chart.data.datasets[1]?.data[colIndex];
      if (!gapMeta.hidden && gapValue != null) {
        const el = gapMeta.data[colIndex];
        if (el) return el.y;
      }
      const realizadoMeta = chart.getDatasetMeta(0);
      if (!realizadoMeta.hidden) {
        const el = realizadoMeta.data[colIndex];
        if (el) return el.y;
      }
      return null;
    };

    ctx.save();
    ctx.font = `bold 14px sans-serif`;
    ctx.fillStyle = colors.textPrimary;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";

    data.labels.forEach((_, i) => {
      const total = getVisibleTotal(i);
      if (total === 0) return;
      const topY = getTopBarY(i);
      if (topY == null) return;
      ctx.fillText(formatM(total), xScale.getPixelForValue(i), topY - 12);
    });
    ctx.restore();

    const inicioMesIndex = chart.options.plugins?.barAnnotations?.inicioMesIndex;
    if (inicioMesIndex == null) return;
    if (!chart.getDatasetMeta(0)?.data?.[inicioMesIndex]) return;

    ctx.save();
    ctx.font = "600 11px sans-serif";
    ctx.fillStyle = colors.textSecondary;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(
      "Mês",
      (chart.chartArea.left + chart.chartArea.right) / 2,
      chart.chartArea.top - 18
    );
    ctx.restore();
  },
};

const tabs = [
  { key: "desconto", label: "Desconto" },
  { key: "volume", label: "Volume" },
];

const plugins = [barAnnotationsPlugin, pluginBarraMinima("linhaTempoBarraMinima", 20)];

const LinhaDoTempo = ({
  descontoLabels = [],
  descontoRealizado = [],
  descontoGap = [],
  volumeLabels = [],
  volumeRealizado = [],
  volumeGap = [],
  inicioMesIndex = null,
  barPercentage = 0.55,
  categoryPercentage = 0.4,
}) => {
  const [activeTab, setActiveTab] = useState("desconto");

  const isDesconto = activeTab === "desconto";
  const labels = isDesconto ? descontoLabels : volumeLabels;
  const realizadoData = isDesconto ? descontoRealizado : volumeRealizado;
  const gapData = isDesconto ? descontoGap : volumeGap;

  const chartData = useMemo(() => {
    const realizado = valoresExibiveis(realizadoData);
    const gap = valoresExibiveis(gapData);
    const hasRealizadoAt = (dataIndex) => realizado[dataIndex] != null;
    const hasGapAt = (dataIndex) => gap[dataIndex] != null;

    return {
      labels,
      datasets: [
        {
          label: "Realizado",
          data: realizado,
          backgroundColor: colors.realized,
          borderRadius: (ctx) => ({
            topLeft: hasGapAt(ctx.dataIndex) ? 0 : 4,
            topRight: hasGapAt(ctx.dataIndex) ? 0 : 4,
            bottomLeft: 4,
            bottomRight: 4,
          }),
          barPercentage,
          categoryPercentage,
        },
        {
          label: "Gap",
          data: gap,
          backgroundColor: colors.gap,
          borderRadius: (ctx) => ({
            topLeft: 4,
            topRight: 4,
            bottomLeft: hasRealizadoAt(ctx.dataIndex) ? 0 : 4,
            bottomRight: hasRealizadoAt(ctx.dataIndex) ? 0 : 4,
          }),
          barPercentage,
          categoryPercentage,
        },
      ],
    };
  }, [labels, realizadoData, gapData, barPercentage, categoryPercentage]);

  const options = useMemo(() => buildOptions(inicioMesIndex), [inicioMesIndex]);

  return (
    <div
      className="bg-white rounded-lg p-6 min-w-0"
      style={{ boxShadow: "0 1px 6px 0 rgba(0,0,0,0.10)" }}
    >
      <div className="flex gap-2 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-1.5 rounded text-sm font-semibold transition-colors ${
              activeTab === tab.key
                ? "bg-[#003DA5] text-white"
                : "bg-white text-[#364153] border border-gray-200 hover:bg-gray-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <h2 className="text-[20px] font-bold mb-2" style={{ color: colors.textPrimary }}>
        Linha do Tempo – {isDesconto ? "Desconto (R$)" : "Volume (t)"}
      </h2>

      <div style={{ height: 300 }}>
        <Bar data={chartData} options={options} plugins={plugins} />
      </div>
    </div>
  );
};

export default LinhaDoTempo;
